import React, { useCallback, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import Text from "@/components/ui/text";
import FullpageBackground from "@/components/layouts/FullpageMessage/FullpageImage.svg";
import { MessageCircleQuestion } from "@dreamhub/designsystem/icons";
import DownloadLogo from "./DownloadLogo.svg";
import { Link } from "@react-email/link";
import Badge from "@/components/ui/badge";
import { useGetAttachment } from "@/entities/attachments/hooks";

const DownloadPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { attachmentId } = useParams<{ attachmentId: string }>();
  const hasDownloaded = useRef(false);

  const { data, isLoading, error, isSuccess, refetch } = useGetAttachment(
    attachmentId ?? ""
  );

  const createLinkAndDownload = useCallback(
    (blobData: Blob, downloadFilename: string) => {
      const blobUrl = URL.createObjectURL(blobData);
      const link = document.createElement("a");
      link.href = blobUrl;
      link.setAttribute("download", downloadFilename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      setTimeout(() => URL.revokeObjectURL(blobUrl), 100);
    },
    []
  );

  useEffect(() => {
    if (isSuccess && data && !hasDownloaded.current) {
      hasDownloaded.current = true;
      createLinkAndDownload(data.blob, data.filename);
    }
  }, [isSuccess, data, createLinkAndDownload]);

  const handleGoHome = () => {
    navigate("/");
  };

  const handleDownloadClick = async () => {
    hasDownloaded.current = false;
    const result = await refetch();
    if (result.data) {
      createLinkAndDownload(result.data.blob, result.data.filename);
    }
  };

  const errorMessage =
    error instanceof Error ? error.message : error ? "Download failed" : null;

  return (
    <div className="flex flex-col items-center justify-center min-h-screen bg-background-primary">
      {/* @ts-ignore */}
      <FullpageBackground className="absolute inset-0 w-full h-full z-[-10]" />

      {/* Main content - side by side layout */}
      <div className="flex items-center justify-center gap-16 px-8">
        {/* Download illustration */}
        <div className="flex-shrink-0">
          {/* @ts-ignore */}
          <DownloadLogo className="w-[280px] h-[280px]" />
        </div>

        {/* Text content */}
        <div className="max-w-md">
          {/* Title */}
          <Text className="mb-3" as="h1" variant="title3H1">
            {t("downloadPage.title")}
          </Text>

          {/* Subtitle */}
          <Text
            className="mb-8 text-text-secondary whitespace-pre-wrap"
            as="p"
            variant="body"
          >
            {t("downloadPage.subtitle")}
          </Text>

          {/* Error message */}
          {error && errorMessage && (
            <Text
              className="mb-4 text-red-500"
              as="p"
              variant="smBodySecondary"
            >
              {errorMessage}
            </Text>
          )}

          {/* Buttons - side by side */}
          <div className="flex items-center gap-3">
            {/* Download button */}
            <Button
              variant="primary"
              onClick={handleDownloadClick}
              disabled={isLoading}
            >
              {isLoading && (
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
              )}
              {t("downloadPage.downloadButton")}
            </Button>

            {/* Go to home button */}
            <Button variant="secondary" onClick={handleGoHome}>
              {t("downloadPage.goHomeButton")}
            </Button>
          </div>
        </div>
      </div>

      {/* Support footer */}
      <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2">
        <Badge
          icon={<MessageCircleQuestion />}
          textVariant="smBodySecondary"
          className="px-4 py-2"
        >
          <Text as="span" variant="smBodySecondary">
            {t("downloadPage.supportText")}{" "}
            <Link href="mailto:support@dreamhub.ai" target="_blank">
              support@dreamhub.ai
            </Link>
          </Text>
        </Badge>
      </div>
    </div>
  );
};

export default DownloadPage;
